import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface PollingIndicatorProps {
  connectionStatus: 'Connecting' | 'Open' | 'Closed' | 'Error';
  isPolling: boolean;
  lastPollingTime: Date | null;
  isManuallyRefreshing: boolean;
  onManualRefresh: () => void;
  executionStatus?: string; // Add this prop to check if execution is completed
  isRealTimeMode?: boolean; // Add this new prop
}

export const PollingIndicator: React.FC<PollingIndicatorProps> = ({
  connectionStatus,
  isPolling,
  lastPollingTime,
  isManuallyRefreshing,
  onManualRefresh,
  executionStatus,
  isRealTimeMode = false // Default to false
}) => {
  const { darkMode } = useTheme();
  
  // Calculate time since last poll
  let timeSinceLastPoll = '';
  if (lastPollingTime) {
    const seconds = Math.round((new Date().getTime() - lastPollingTime.getTime()) / 1000);
    timeSinceLastPoll = `${seconds}s ago`;
  }
  
  // If execution is completed, show a different message
  const isCompleted = executionStatus === 'completed';
  
  // Determine the status message and color based on connection status and real-time mode
  let statusMessage = '';
  let statusColor = '';
  
  if (isCompleted) {
    statusMessage = 'Execution completed, no further updates needed';
    statusColor = 'bg-green-500';
  } else if (isRealTimeMode && connectionStatus === 'Open') {
    statusMessage = 'Real-time updates active (WebSocket)';
    statusColor = 'bg-green-500 animate-pulse';
  } else if (connectionStatus === 'Open') {
    statusMessage = 'Real-time updates available';
    statusColor = 'bg-green-500';
  } else if (connectionStatus === 'Connecting') {
    statusMessage = 'Connecting to real-time updates...';
    statusColor = 'bg-yellow-500 animate-pulse';
  } else if (isPolling) {
    statusMessage = `Polling for updates (last update: ${timeSinceLastPoll})`;
    statusColor = 'bg-blue-500';
  } else {
    statusMessage = 'Connection closed, using polling for updates';
    statusColor = 'bg-red-500';
  }
  
  return (
    <div className={`connection-status flex items-center justify-between ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-sm mt-2 p-2 rounded ${
      isCompleted ? darkMode ? 'bg-green-900/20' : 'bg-green-50' :
      isRealTimeMode && connectionStatus === 'Open' ? darkMode ? 'bg-green-900/30' : 'bg-green-50' :
      connectionStatus === 'Open' ? darkMode ? 'bg-green-900/20' : 'bg-green-50' : 
      connectionStatus === 'Connecting' ? darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50' : 
      isPolling ? darkMode ? 'bg-blue-900/20' : 'bg-blue-50' : 
      darkMode ? 'bg-red-900/20' : 'bg-red-50'
    }`}>
      <div className="flex items-center">
        <div className={`w-3 h-3 rounded-full mr-2 ${statusColor}`} />
        <div>
          {statusMessage}
        </div>
      </div>
      
      {/* Add manual refresh button when polling is active or connection failed */}
      {!isCompleted && !isRealTimeMode && (isPolling || connectionStatus === 'Error' || connectionStatus === 'Closed') && (
        <button
          className={`ml-2 px-3 py-1 text-xs rounded ${
            darkMode 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          } ${isManuallyRefreshing ? 'opacity-50 cursor-wait' : ''}`}
          onClick={onManualRefresh}
          disabled={isManuallyRefreshing}
        >
          {isManuallyRefreshing ? 'Refreshing...' : 'Refresh Now'}
        </button>
      )}
    </div>
  );
};
